import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import * as calendarOfferActions from 'actions/calendarOfferActions';
import * as searchUnitActions from 'actions/searchUnitActions';
import * as customControlActions from 'actions/customControlActions';
import * as annualAvailabilityActions from 'actions/annualAvailabilityActions';

import { MONTH_FLEXIBILITY_VALUE } from 'constants/searchUnit';
import {
  getMonthToShowForCalendar,
} from 'utils/utils';
import { HOTEL_ONLY_INDEX_MAX_SUPPORTED_DAYS } from 'utils/dates';

import SearchUnitCustomControlCalendar from 'components/SearchUnitCustomControlCalendar/SearchUnitCustomControlCalendar';
import SearchUnitCustomControlCalendarToggle from 'components/SearchUnitCustomControlCalendar/SearchUnitCustomControlCalendarToggle';
import DatePickerWithPrices from 'components/DatePickerWithPrices/DatePickerWithPrices';

const mapStateToProps = state => ({
  appState: state.appState,
  annualAvailability: state.annualAvailability,
  baseState: state.baseState,
  hotelDetails: state.hotelDetails,
  dimensionsEntities: state.dimensionsEntities,
  dimensionsResult: state.dimensionsResult,
  featureFlags: state.featureFlags,
});

const mapDispatchToProps = dispatch => ({
  calendarOfferActionsBound: bindActionCreators(calendarOfferActions, dispatch),
  searchUnitActionsBound: bindActionCreators(searchUnitActions, dispatch),
  customControlActionsBound: bindActionCreators(customControlActions, dispatch),
  annualAvailabilityActionsBound: bindActionCreators(annualAvailabilityActions, dispatch),
});

export class SearchUnitCalendarLayout extends Component {
  static propTypes = {
    appState: PropTypes.object.isRequired,
    annualAvailability: PropTypes.object.isRequired,
    baseState: PropTypes.object.isRequired,
    hotelDetails: PropTypes.object.isRequired,
    calendarOfferActionsBound: PropTypes.shape({
      calDateChanged: PropTypes.func.isRequired,
    }).isRequired,
    searchUnitActionsBound: PropTypes.shape({
      setDepartureDate: PropTypes.func.isRequired,
      setDepartureDateAndFlexibility: PropTypes.func.isRequired,
      clearDepartureDate: PropTypes.func.isRequired,
      clearDepartureDateAndFlexibility: PropTypes.func.isRequired,
    }).isRequired,
    dimensionsEntities: PropTypes.object,
    dimensionsResult: PropTypes.object,
    loadDayOfMonthData: PropTypes.func,
    customControlActionsBound: PropTypes.object,
    annualAvailabilityActionsBound: PropTypes.object,
    selectedDate: PropTypes.number,
    isHotelOnly: PropTypes.bool,
    showPricing: PropTypes.bool,
    featureFlags: ImmutablePropTypes.map.isRequired,
    isWholeMonthCalendarEnabled: PropTypes.bool,
  };

  static defaultProps = {
    isHotelOnly: false,
    showPricing: false,
    isWholeMonthCalendarEnabled: false,
  };

  onCalendarDateClick = (calDate) => {
    const {
      calendarOfferActionsBound,
      searchUnitActionsBound,
      customControlActionsBound,
      baseState,
      isWholeMonthCalendarEnabled,
    } = this.props;

    calendarOfferActionsBound.calDateChanged();

    const selectedFlexibility = baseState.getIn(['queryState', 'props', 'deptDateFlexibility', 'value']);
    if (isWholeMonthCalendarEnabled && selectedFlexibility === MONTH_FLEXIBILITY_VALUE) {
      searchUnitActionsBound.setDepartureDateAndFlexibility(calDate, 0);
    } else {
      searchUnitActionsBound.setDepartureDate(calDate);
    }
    customControlActionsBound.hideControl();
  };

  onCalendarMonthClick = (calDate) => {
    const {
      calendarOfferActionsBound,
      customControlActionsBound,
      searchUnitActionsBound,
      showPricing,
    } = this.props;

    calendarOfferActionsBound.calDateChanged();
    searchUnitActionsBound.setDepartureDateAndFlexibility(calDate, MONTH_FLEXIBILITY_VALUE);

    if (!showPricing) {
      customControlActionsBound.hideControl();
    }
  };

  onClearDate = () => {
    const { searchUnitActionsBound, isWholeMonthCalendarEnabled } = this.props;
    if (isWholeMonthCalendarEnabled) {
      searchUnitActionsBound.clearDepartureDateAndFlexibility();
    } else {
      searchUnitActionsBound.clearDepartureDate();
    }
  };

  get availabilityList() {
    const { dimensionsEntities, dimensionsResult } = this.props;
    const dayOfMonthAvailabilityIds = dimensionsResult.get('D_DayOfMonth') || Immutable.List();
    return dayOfMonthAvailabilityIds.map(id => dimensionsEntities.get(`${id}`) && dimensionsEntities.getIn([`${id}`, 'available']));
  }

  displayPerNightPricing() {
    const { featureFlags, appState, hotelDetails } = this.props;
    const matchThirdPartyPricingOnCalendar = featureFlags.get('HotelOnlyMatchThirdPartyPricesOnCalendar', false);
    const isHotelIndexed = hotelDetails.getIn(['hotelOnly', 'indexed'], false);
    const hasThirdPartyChannel = appState.get('hasThirdPartyChannelParamHO') && !isHotelIndexed;

    return !!hasThirdPartyChannel && matchThirdPartyPricingOnCalendar;
  }

  render() {
    const {
      annualAvailability,
      annualAvailabilityActionsBound,
      appState,
      baseState,
      customControlActionsBound,
      dimensionsEntities,
      dimensionsResult,
      featureFlags,
      hotelDetails,
      isHotelOnly,
      isWholeMonthCalendarEnabled,
      loadDayOfMonthData,
      searchUnitActionsBound,
      selectedDate,
      showPricing,
    } = this.props;

    const selectedFlexibility = baseState.getIn(['queryState', 'props', 'deptDateFlexibility', 'value']);
    const duration = baseState.getIn(['queryState', 'props', 'duration', 'value']);

    const showCalendarWithPricing = showPricing && (duration <= HOTEL_ONLY_INDEX_MAX_SUPPORTED_DAYS);
    const showCalendarWithWholeMonth = isWholeMonthCalendarEnabled && !showPricing;
    const showDefaultCalendar = !isWholeMonthCalendarEnabled && (!showPricing || duration > HOTEL_ONLY_INDEX_MAX_SUPPORTED_DAYS);

    return (
      <div className="search-unit-calendar-layout">
        { showCalendarWithPricing && (
          <DatePickerWithPrices
            annualAvailability={annualAvailability}
            annualAvailabilityType="ho"
            appState={appState}
            baseState={baseState}
            displayPerNightPricing={this.displayPerNightPricing()}
            featureFlags={featureFlags}
            hotelDetail={hotelDetails}
            handleDayClick={this.onCalendarDateClick}
            handleMonthClick={this.onCalendarMonthClick}
            annualAvailabilityActionsBound={annualAvailabilityActionsBound}
            searchUnitActionsBound={searchUnitActionsBound}
            customControlActionsBound={customControlActionsBound}
            numberOfChargeablePassengers={baseState.get('numberOfChargeablePassengers')}
          />
        )}

        { showCalendarWithWholeMonth && (
          <SearchUnitCustomControlCalendarToggle
            availability={this.availabilityList}
            dimensionsEntities={dimensionsEntities}
            dimensionsResult={dimensionsResult}
            handleDayClick={this.onCalendarDateClick}
            handleMonthClick={this.onCalendarMonthClick}
            hideLegend={isHotelOnly}
            loadDayOfMonthData={loadDayOfMonthData}
            monthViewDayStamp={getMonthToShowForCalendar(selectedDate)}
            selectedCalendarDayStamp={selectedDate}
            selectedFlexibility={selectedFlexibility}
            type="search-unit"
          />
        )}

        { showDefaultCalendar && (
          <SearchUnitCustomControlCalendar
            availability={this.availabilityList}
            dimensionsEntities={dimensionsEntities}
            dimensionsResult={dimensionsResult}
            handleDayClick={this.onCalendarDateClick}
            hideLegend={isHotelOnly}
            isHotelOnly={isHotelOnly}
            loadDayOfMonthData={loadDayOfMonthData}
            monthViewDayStamp={getMonthToShowForCalendar(selectedDate)}
            selectedCalendarDayStamp={selectedDate}
            type="search-unit"
          />
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchUnitCalendarLayout);



/** WEBPACK FOOTER **
 ** ./src/layouts/SearchUnitCalendarLayout/SearchUnitCalendarLayout.jsx
 **/